<template>
  <div>
    <ModalComponent
      name="attachmentList"
      class="rouded-full"
      width="500"
      height="auto"
      :adaptive="true"
      :click-to-close="false"
    >
      <ModalContainer
        title="Attachments"
        @close="hideAttachmentModal"
      >
        <div class="flex justify-end mb-2 space-x-2">
          <PrimaryButton
            default-bg-colors="bg-main-primary hover:main-primary active:bg-main-primary"
            text-color="text-black"
            @click="onDownloadAll"
          >
            Download All
          </PrimaryButton>
          <PrimaryButton
            v-if="canUnlink"
            default-bg-colors="bg-main-primary hover:bg-main-primary active:bg-main-primary"
            text-color="text-black"
            @click="onUnlinkAll"
          >
            Unlink All
          </PrimaryButton>
        </div>
        <div class="space-y-1">
          <DataPointAttachmentContainer
            v-for="(file, index) in files"
            :key="index"
            :file="file"
            :can-unlink="canUnlink"
            @download="onDownload(file)"
            @unlink="onUnlink(file)"
          />
        </div>
      </ModalContainer>
    </ModalComponent>

    <ModalComponent
      name="confirmDialog"
      class="rouded-full"
      width="500"
      height="auto"
      :adaptive="true"
      :click-to-close="false"
    >
      <div
        v-if="confirmTitle"
        class="fixed inset-0 z-[999] overflow-y-auto"
        :aria-labelledby="confirmTitle"
        role="dialog"
        aria-modal="true"
      >
        <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
            aria-hidden="true"
          />
          <!-- This element is to trick the browser into centering the dialog contents. -->
          <span
            class="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >&#8203;</span>
          <div
            class="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white border rounded-lg shadow-xl border-emerald-200 sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
          >
            <div class="mt-3 text-center sm:mt-5">
              <h3 class="text-lg font-medium leading-6 text-gray-900">
                {{ confirmTitle }}
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  {{ confirmContent }}
                </p>
              </div>
            </div>
            <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
              <button
                type="button"
                class="inline-flex items-center justify-center w-full brand-button-light gap-x-1 disabled:cursor-not-allowed disabled:opacity-80 sm:col-start-2"
                :disabled="confirmLoading"
                @click="onConfirm(true)"
              >
                <span v-if="confirmLoading">
                  <SpinnerLarge />
                </span>
                <span>{{ confirmLoading ? "Loading" : "Confirm" }}</span>
              </button>
              <button
                type="button"
                :disabled="confirmLoading"
                class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-red-700 bg-white border border-red-300 rounded shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                @click="onConfirm(false)"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalComponent>
  </div>
</template>

<script>
export default {
  name: 'AttachmentListModal',

  props: {
    files: {
      type: Array,
      default: () => [],
    },
    rowIndex: {
      type: Number,
      default: 0,
    },
    canUnlink: {
      type: Boolean,
      default: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
  },

  emits: ['download', 'unlink-all', 'attach-closed', 'unlink'],

  data() {
    return {
      confirmTitle: null,
      selectedFile: null,
      confirmContent: null,
      isConfirmed: false,
      confirmLoading: false,
      confirmSource: null,
    }
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.$modal.show('attachmentList')
      }
      else {
        this.$modal.hide('attachmentList')
      }
    },
  },
  methods: {
    async onDownload(file) {
      try {
        await this.downloadFile(file)
      }
      catch (error) {
        this.$toast.error(error.message || 'Unable to download file', {
          timeout: 2000,
          position: 'top-right',
        })
      }
    },

    onDownloadAll() {
      let files = this.files
      if (Array.isArray(files) && typeof files[0] === 'object') {
        files = files.map(file => file.document_name)
      }

      const cid = localStorage.getItem('cid')
      useNuxtApp().$apis.esgMetrics
        .downloadAllDocument(cid, files)
        .then((response) => {
          const filename = 'all_files.zip'
          const blob = new Blob([response.data], { type: 'application/zip' })
          const link = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = link
          a.download = filename
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(link)
        })
        .catch((error) => {
          useNuxtApp().$toast.error(error.message || 'Unable to download files', {
            timeout: 2000,
            position: 'top-right',
          })
        })
      useNuxtApp().$modal.hide('attachmentList')
      this.$emit('attach-closed')
    },
    onUnlink(file) {
      this.selectedFile = file
      if (this.isConfirmed) {
        this.$emit('unlink', { document: this.selectedFile, index: this.rowIndex })
        this.confirmTitle = null
        this.confirmContent = null
        this.isConfirmed = false
      }
      else {
        this.confirmAction('Confirm', 'Are you sure you want unlink attachments?')
      }
    },
    onUnlinkAll() {
      if (this.isConfirmed) {
        this.$emit('unlink-all', { index: this.rowIndex, supporting_documents: [], row_update: true })
        useNuxtApp().$modal.hide('attachmentList')
        this.$emit('attach-closed')
        this.confirmTitle = null
        this.confirmContent = null
        this.isConfirmed = false
      }
      else {
        this.confirmSource = 'unlinkall'
        this.confirmAction('Confirm', 'Are you sure to unlink all documents? \nData point(s) associated with this document will be removed.')
      }
    },

    async downloadFile(data) {
      this.confirmTitle = null
      this.confirmContent = null
      this.isConfirmed = false
      const { document_name, file } = data
      const fileName = document_name || file
      const cid = localStorage.getItem('cid')
      const queryParams = { type: 'ghg' }
      const response = await useNuxtApp().$apis.esgMetrics.downloadDocument(cid, fileName, queryParams)

      const filename = fileName
      const fileExtension = this.getFileExtension(filename)
      const mimeType = this.mimeTypeMapping()[fileExtension] || 'application/octet-stream'

      const blob = new Blob([response.data], { type: mimeType })
      const link = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = link
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(link)
    },

    mimeTypeMapping() {
      return {
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv',
        pdf: 'application/pdf',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        bmp: 'image/bmp',
      }
    },

    getFileExtension(filename) {
      return filename.split('.').pop()
    },
    onConfirm(confirm) {
      this.isConfirmed = confirm
      this.$modal.hide('confirmDialog')
      this.confirmTitle = null
      this.confirmContent = null
      if (this.confirmSource === 'unlinkall') {
        this.onUnlinkAll()
      }
      else if (this.isConfirmed) {
        this.onUnlink(this.selectedFile)
      }
    },
    confirmAction(title, content) {
      this.confirmTitle = title
      this.confirmContent = content
      this.$modal.show('confirmDialog')
    },
    hideAttachmentModal() {
      this.$modal.hide('attachmentList')
      this.$emit('attach-closed')
    },
  },
}
</script>
